import React, { useMemo, useState, useEffect } from "react";
import { Search, Download, ChevronLeft, ChevronRight, Power, PowerOff, Trash2, X, Check, RotateCcw } from "lucide-react";
import { DataTable } from "@/components/kit/DataTable";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { CountrySelect } from "@/components/kit/CountrySelect";
import { MultiSelect } from "@/components/kit/MultiSelect";
import { enterToAction } from "@/lib/enterAction";

const PAGE_SIZES = [10, 25, 50, 100, 200];

function readSize(testId) {
  try {
    const v = Number(localStorage.getItem(`dtp-size-${testId || "default"}`));
    return PAGE_SIZES.includes(v) ? v : 10;
  } catch { return 10; }
}

import { fmtDateTime } from "@/lib/format";

const ISO_DT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
function cellValue(col, row) {
  let v = col.exportValue ? col.exportValue(row) : row[col.sortField || col.key];
  if (v == null) return "";
  // Export datetimes exactly as the user sees them on the platform (display timezone).
  if (typeof v === "string" && ISO_DT.test(v)) return fmtDateTime(v);
  return String(v);
}

function download(name, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export function DataTablePro({
  columns, rows = [], loading, onRowClick, rowActions, emptyText, testId,
  searchKeys, filters = [], exportName = "export", searchPlaceholder = "Search…",
  toolbarRight, selectable = false, onBulkAction, bulkActions = ["enable", "disable", "delete"], bulkExtra,
  customFilters, customFiltersEnd, filterFn, initialFilters = {}, initialCustom = {},
  serverMode = false, serverTotal, onServerChange, fetchAllRows,
}) {
  // Draft = what the user is editing; applied = what actually filters the table.
  const [draftQ, setDraftQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [draftCustom, setDraftCustom] = useState(initialCustom);
  const [appliedCustom, setAppliedCustom] = useState(initialCustom);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(() => readSize(testId));
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const sKeys = searchKeys || columns.map((c) => c.key);

  // The search box is always present, so Apply/Reset are always shown for a consistent,
  // platform-wide behaviour (Enter = Apply everywhere).
  const hasFilterControls = true;
  const dirty = draftQ !== appliedQ
    || JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters)
    || JSON.stringify(draftCustom) !== JSON.stringify(appliedCustom);

  const applyFilters = () => {
    setAppliedQ(draftQ); setAppliedFilters(draftFilters); setAppliedCustom(draftCustom); setPage(1);
  };
  const resetFilters = () => {
    setDraftQ(""); setAppliedQ(""); setDraftFilters({}); setAppliedFilters({});
    setDraftCustom({}); setAppliedCustom({}); setPage(1);
  };

  useEffect(() => {
    try { localStorage.setItem(`dtp-size-${testId || "default"}`, String(size)); } catch { /* ignore */ }
  }, [size, testId]);

  const filtered = useMemo(() => {
    let out = rows;
    if (!serverMode) {
      const term = appliedQ.trim().toLowerCase();
      if (term) out = out.filter((r) => sKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(term)));
      filters.forEach((f) => {
        const val = appliedFilters[f.key];
        if (f.multi) {
          if (Array.isArray(val) && val.length) {
            const set = new Set(val.map(String));
            out = out.filter((r) => set.has(String(f.getValue ? f.getValue(r) : r[f.key])));
          }
        } else if (val && val !== "all") {
          out = out.filter((r) => String(f.getValue ? f.getValue(r) : r[f.key]) === val);
        }
      });
      if (filterFn) out = out.filter((r) => filterFn(r, appliedCustom));
    }
    if (sortKey && !serverMode) {
      const col = columns.find((c) => (c.sortField || c.key) === sortKey);
      out = [...out].sort((a, b) => {
        const va = col?.sortValue ? col.sortValue(a) : a[sortKey];
        const vb = col?.sortValue ? col.sortValue(b) : b[sortKey];
        if (va == null) return 1; if (vb == null) return -1;
        const r = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
        return sortDir === "asc" ? r : -r;
      });
    }
    return out;
  }, [rows, appliedQ, appliedFilters, appliedCustom, filters, filterFn, sortKey, sortDir, columns, sKeys, serverMode]);

  const total = serverMode ? (serverTotal ?? filtered.length) : filtered.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const curPage = serverMode && serverTotal == null ? page : Math.min(page, pages);
  const pageRows = serverMode ? filtered : filtered.slice((curPage - 1) * size, curPage * size);

  // Server mode: the parent fetches exactly one page; report every page/size/filter/sort change.
  useEffect(() => {
    if (!serverMode || !onServerChange) return;
    onServerChange({ page: curPage, size, q: appliedQ, filters: appliedFilters, custom: appliedCustom, sort: sortKey, dir: sortDir });
  }, [serverMode, onServerChange, curPage, size, appliedQ, appliedFilters, appliedCustom, sortKey, sortDir]);

  // Bulk selection is PAGE-SCOPED: the header checkbox and bulk actions only ever cover the
  // rows visible on the current page (pagination-aware), never the whole filtered set.
  const pageIds = useMemo(() => pageRows.map((r) => r.id).filter(Boolean), [pageRows]);
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(pageIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [pageIds]);
  const allSelected = selectable && pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    return next;
  });
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const runBulk = async (action) => {
    if (!onBulkAction || selected.size === 0) return;
    setBulkBusy(true);
    try { await onBulkAction(action, [...selected]); }
    finally { setBulkBusy(false); clearSelection(); }
  };

  const onSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
    if (serverMode) setPage(1);
  };

  const doExport = async (fmt, scope = "all") => {
    let source;
    if (scope === "page") source = pageRows;
    else if (serverMode && fetchAllRows) {
      try { source = await fetchAllRows(); } catch { source = filtered; }
    } else source = filtered;
    const headers = columns.map((c) => c.label);
    const matrix = (source || []).map((r) => columns.map((c) => cellValue(c, r)));
    if (fmt === "csv") {
      const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
      const csv = [headers, ...matrix].map((row) => row.map(esc).join(",")).join("\n");
      download(`${exportName}.csv`, "text/csv;charset=utf-8", csv);
    } else {
      const td = (s) => `<td>${String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`;
      const html = `<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${matrix.map((r) => `<tr>${r.map(td).join("")}</tr>`).join("")}</tbody></table>`;
      download(`${exportName}.xls`, "application/vnd.ms-excel", html);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") applyFilters(); };

  return (
    <div className="space-y-3" data-testid={`${testId}-pro`}>
      {/* Filter toolbar — compact, premium, wraps gracefully; controls keep a fixed width so a
          page with few filters never stretches them across the screen. */}
      <div className="rounded-xl border border-white/[0.06] bg-[#0E0E10] px-3 py-2.5" data-testid={`${testId}-filterbar`}
        onKeyDown={enterToAction(applyFilters)}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full min-w-[140px] sm:w-auto sm:max-w-[210px] sm:flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input value={draftQ} onChange={(e) => setDraftQ(e.target.value)} onKeyDown={onKey} placeholder={searchPlaceholder}
              data-testid={`${testId}-search`}
              className="h-9 w-full rounded-lg border border-white/[0.1] bg-[#0A0A0A] pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors hover:border-white/20 focus:border-white/25" />
          </div>
          {customFilters && customFilters({ values: draftCustom, set: (k, v) => setDraftCustom((s) => ({ ...s, [k]: v })), onEnter: onKey })}
          {filters.map((f) => (
            <div key={f.key} className="w-full sm:w-[156px]">
              {f.multi ? (
                <MultiSelect values={Array.isArray(draftFilters[f.key]) ? draftFilters[f.key] : []}
                  onChange={(vals) => setDraftFilters((s) => ({ ...s, [f.key]: vals }))}
                  options={f.options || []} country={!!f.country} label={f.label}
                  searchPlaceholder={`Search ${f.label.toLowerCase()}...`} testId={`${testId}-filter-${f.key}`}
                  widthClass="w-[240px]" triggerClass="h-9 rounded-lg" />
              ) : f.country ? (
                <CountrySelect value={draftFilters[f.key] && draftFilters[f.key] !== "all" ? draftFilters[f.key] : ""}
                  onChange={(v) => setDraftFilters((s) => ({ ...s, [f.key]: v || "all" }))}
                  allowClear placeholder={`${f.label}: All`} testId={`${testId}-filter-${f.key}`} triggerClass="h-9 rounded-lg" />
              ) : f.searchable ? (
                <SearchSelect value={draftFilters[f.key] || "all"}
                  onChange={(v) => setDraftFilters((s) => ({ ...s, [f.key]: v }))}
                  options={[{ value: "all", label: `${f.label}: All`, search: "all" }, ...f.options.map((o) => ({ ...o, search: o.search || o.label }))]}
                  placeholder={f.label} searchPlaceholder={`Search ${f.label.toLowerCase()}...`} testId={`${testId}-filter-${f.key}`} widthClass="w-[240px]" triggerClass="h-9 rounded-lg" />
              ) : (
                <Select value={draftFilters[f.key] || "all"} onValueChange={(v) => setDraftFilters((s) => ({ ...s, [f.key]: v }))}>
                  <SelectTrigger className="h-9 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-sm" data-testid={`${testId}-filter-${f.key}`}>
                    <SelectValue placeholder={f.label} />
                  </SelectTrigger>
                  <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                    <SelectItem value="all">{f.label}: All</SelectItem>
                    {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
          {customFiltersEnd && customFiltersEnd({ values: draftCustom, set: (k, v) => setDraftCustom((s) => ({ ...s, [k]: v })), onEnter: onKey })}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {hasFilterControls && (
              <>
                <Button size="sm" onClick={applyFilters} data-testid={`${testId}-apply-filters`} className="h-9 gap-1.5 rounded-lg bg-white px-3.5 text-black hover:bg-white/90">
                  <Check className="h-3.5 w-3.5" /> Apply{dirty ? " •" : ""}
                </Button>
                <Button size="sm" variant="ghost" onClick={resetFilters} title="Reset filters" data-testid={`${testId}-reset-filters`} className="h-9 gap-1.5 rounded-lg px-2.5 text-sm text-muted-foreground hover:text-foreground">
                  <RotateCcw className="h-3.5 w-3.5" /><span className="hidden min-[1460px]:inline">Reset</span>
                </Button>
                <span className="mx-0.5 hidden h-5 w-px bg-white/10 sm:block" />
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" title="Export" className="h-9 gap-2 rounded-lg border-white/[0.1] text-sm" data-testid={`${testId}-export`}><Download className="h-3.5 w-3.5" /><span className="hidden min-[1460px]:inline">Export</span></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-white/[0.1] bg-[#1a1a1e]">
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">All records (current filters)</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => doExport("csv", "all")} data-testid={`${testId}-export-csv`} className="cursor-pointer">CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("xls", "all")} data-testid={`${testId}-export-excel`} className="cursor-pointer">Excel</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Current page only</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => doExport("csv", "page")} data-testid={`${testId}-export-csv-page`} className="cursor-pointer">CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("xls", "page")} data-testid={`${testId}-export-excel-page`} className="cursor-pointer">Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {toolbarRight}
          </div>
        </div>
      </div>

      {selectable && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.12] bg-[#1a1a1e] px-3 py-2" data-testid={`${testId}-bulk-bar`}>
          <span className="text-sm text-foreground" data-testid={`${testId}-bulk-count`}>{selected.size} selected</span>
          <div className="ml-2 flex items-center gap-1.5">
            {bulkActions.includes("enable") && (
              <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => runBulk("enable")} data-testid={`${testId}-bulk-enable`}
                className="h-8 gap-1.5 border-white/[0.12] text-xs"><Power className="h-3.5 w-3.5" /> Enable</Button>
            )}
            {bulkActions.includes("disable") && (
              <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => runBulk("disable")} data-testid={`${testId}-bulk-disable`}
                className="h-8 gap-1.5 border-white/[0.12] text-xs"><PowerOff className="h-3.5 w-3.5" /> Disable</Button>
            )}
            {bulkActions.includes("delete") && (
              <Button size="sm" disabled={bulkBusy} onClick={() => runBulk("delete")} data-testid={`${testId}-bulk-delete`}
                className="h-8 gap-1.5 bg-[#EF4444] text-xs text-white hover:bg-[#dc2626]"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
            )}
            {bulkExtra && bulkExtra({ ids: [...selected], clear: clearSelection, busy: bulkBusy, setBusy: setBulkBusy })}
          </div>
          <button onClick={clearSelection} data-testid={`${testId}-bulk-clear`} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        </div>
      )}

      <DataTable columns={columns.map((c) => ({ ...c, sortable: c.sortable }))} rows={pageRows} loading={loading}
        onRowClick={onRowClick} rowActions={rowActions} emptyText={emptyText} testId={testId}
        sortKey={sortKey} sortDir={sortDir} onSort={onSort}
        selectable={selectable} selectedIds={selected} onToggleRow={toggleRow} allSelected={allSelected} onToggleAll={toggleAll} />

      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>Rows per page</span>
          <Select value={String(size)} onValueChange={(v) => { setSize(Number(v)); setPage(1); }}>
            <SelectTrigger className="h-8 w-[80px] border-white/[0.1] bg-[#0A0A0A] text-xs" data-testid={`${testId}-page-size`}><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
              {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <span data-testid={`${testId}-total`}>{total} records</span>
        </div>
        <div className="flex items-center gap-2">
          <button disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid={`${testId}-prev`}
            className="rounded-md border border-white/[0.1] p-1.5 disabled:opacity-30 hover:bg-white/[0.05]"><ChevronLeft className="h-3.5 w-3.5" /></button>
          <span>Page {curPage} / {pages}</span>
          <button disabled={curPage >= pages} onClick={() => setPage(curPage + 1)} data-testid={`${testId}-next`}
            className="rounded-md border border-white/[0.1] p-1.5 disabled:opacity-30 hover:bg-white/[0.05]"><ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}
